import { useEffect, useMemo, useRef, useState } from 'react';
import { AsYouType, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/mobile';
import PL from 'country-flag-icons/react/3x2/PL';
import IT from 'country-flag-icons/react/3x2/IT';
import FR from 'country-flag-icons/react/3x2/FR';
import BE from 'country-flag-icons/react/3x2/BE';
import NL from 'country-flag-icons/react/3x2/NL';
import LU from 'country-flag-icons/react/3x2/LU';
import ES from 'country-flag-icons/react/3x2/ES';
import PT from 'country-flag-icons/react/3x2/PT';
import {
  PHONE_COUNTRIES,
  WORLD_PHONE_COUNTRIES,
  type PhoneCountry,
  type PhoneIso,
} from '../util/phoneCountries';

const FLAG_BY_ISO = { PL, IT, FR, BE, NL, LU, ES, PT } as const satisfies Record<PhoneIso, unknown>;

type Variant = 'shop' | 'admin';

/**
 * `inpost` is the carrier's own reach: a number outside it cannot go on an InPost label.
 * `world` is for a parcel the shop hands to another carrier, where the only constraint is that
 * the number is real.
 */
type Scope = 'inpost' | 'world';

type PhoneInputProps = {
  value: string;
  onChange: (e164: string, valid: boolean) => void;
  variant?: Variant;
  scope?: Scope;
  autoFocus?: boolean;
  id?: string;
  disabled?: boolean;
};

function countriesFor(scope: Scope): readonly PhoneCountry[] {
  return scope === 'world' ? WORLD_PHONE_COUNTRIES : PHONE_COUNTRIES;
}

function isoFromValue(
  value: string,
  countries: readonly PhoneCountry[],
): { iso: string; national: string } {
  const fallback = countries[0]!.iso;
  if (!value) return { iso: fallback, national: '' };
  const parsed = parsePhoneNumberFromString(value);
  if (parsed?.country && countries.some((c) => c.iso === parsed.country)) {
    return { iso: parsed.country, national: parsed.formatNational() };
  }
  return { iso: fallback, national: '' };
}

export function PhoneInput({
  value,
  onChange,
  variant = 'shop',
  scope = 'inpost',
  autoFocus,
  id,
  disabled,
}: PhoneInputProps) {
  const countries = countriesFor(scope);
  // Re-parsing on every render would steal the cursor mid-edit ("+48 5" →
  // reparse → "5" loses focus). Track the last E.164 we emitted and only
  // re-derive when the parent passes a value we didn't produce ourselves.
  const initial = useMemo(() => isoFromValue(value, countries), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [iso, setIso] = useState<string>(initial.iso);
  const [national, setNational] = useState(initial.national);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(() =>
    Math.max(0, countries.findIndex((c) => c.iso === initial.iso)),
  );

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastEmittedRef = useRef(value);

  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    const next = isoFromValue(value, countries);
    setIso(next.iso);
    setNational(next.national);
    lastEmittedRef.current = value;
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Narrowing the scope can strand the buyer on a country the new one does not reach - switching
  // a foreign order back to a paczkomat leaves a German number InPost will not take. Dropping it
  // says so on the spot instead of at the 400 after submitting.
  useEffect(() => {
    if (countries.some((c) => c.iso === iso)) return;
    const fallback = countries[0]!.iso;
    setIso(fallback);
    setNational('');
    lastEmittedRef.current = '';
    onChange('', false);
  }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const emit = (nextIso: string, nextNational: string) => {
    const parsed = parsePhoneNumberFromString(nextNational, nextIso as CountryCode);
    const e164 = parsed?.number ?? '';
    lastEmittedRef.current = e164;
    onChange(e164, parsed?.isValid() ?? false);
  };

  const onNationalChange = (raw: string) => {
    const formatted = new AsYouType(iso as CountryCode).input(raw);
    setNational(formatted);
    emit(iso, formatted);
  };

  const pickIso = (nextIso: string) => {
    setIso(nextIso);
    setOpen(false);
    const reformatted = new AsYouType(nextIso as CountryCode).input(national);
    setNational(reformatted);
    emit(nextIso, reformatted);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onTriggerKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, countries.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const next = countries[highlight];
      if (next) pickIso(next.iso);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const c = variant === 'shop' ? SHOP : ADMIN;
  const current = countries.find((x) => x.iso === iso) ?? countries[0]!;

  const hasInput = national.trim().length > 0;
  const parsedForValidity = useMemo(
    () => parsePhoneNumberFromString(national, iso as CountryCode),
    [national, iso],
  );
  const invalid = hasInput && !(parsedForValidity?.isValid() ?? false);

  const rowStyle: React.CSSProperties = invalid
    ? { ...c.row, outline: `2px solid ${ERROR_COLOR}`, outlineOffset: -2 }
    : c.row;

  return (
    <div ref={wrapperRef} style={c.wrap}>
      <div style={rowStyle}>
        <button
          type="button"
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={onTriggerKey}
          style={c.trigger}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`Kraj: ${current.name}`}
          disabled={disabled}
        >
          <CountryMark iso={iso} style={c.flag} />
          <span style={c.dial}>{current.dial}</span>
          <span style={c.caret} aria-hidden>▾</span>
        </button>
        <input
          ref={inputRef}
          id={id}
          className={variant === 'shop' ? 'input' : 'input-a'}
          style={c.input}
          value={national}
          onChange={(e) => onNationalChange(e.target.value)}
          inputMode="tel"
          autoComplete="tel"
          autoFocus={autoFocus}
          placeholder="numer telefonu"
          disabled={disabled}
          aria-invalid={invalid || undefined}
        />
      </div>

      {invalid && (
        <div
          className="mono"
          style={{
            marginTop: 6,
            fontSize: 12,
            color: ERROR_COLOR,
            letterSpacing: '.02em',
          }}
        >
          Numer nieprawidłowy
        </div>
      )}

      {open && (
        <ul style={c.list} role="listbox" aria-label="Kraj numeru telefonu">
          {countries.map((country, i) => {
            const active = i === highlight;
            const selected = iso === country.iso;
            return (
              <li key={country.iso} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pickIso(country.iso);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  style={{
                    ...c.option,
                    ...(active ? c.optionActive : null),
                    ...(selected ? c.optionSelected : null),
                  }}
                >
                  <CountryMark iso={country.iso} style={c.flag} />
                  <span style={c.optionName}>{country.name}</span>
                  <span style={c.optionDial}>{country.dial}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Flag artwork is bundled per country, and the worldwide scope has 241 of them. Outside the
// InPost eight the code itself identifies the country well enough to sit next to its name.
function CountryMark({ iso, style }: { iso: string; style: React.CSSProperties }) {
  const Flag = FLAG_BY_ISO[iso as PhoneIso] as
    | React.ComponentType<{ style?: React.CSSProperties; title?: string }>
    | undefined;
  if (Flag) return <Flag style={style} title={iso} />;
  return (
    <span style={{ ...style, ...isoMark }} title={iso}>
      {iso}
    </span>
  );
}

// ----------------------------- styles -----------------------------

const ERROR_COLOR = '#c0392b';

const isoMark: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'var(--mono)',
  fontSize: 9,
  letterSpacing: '.04em',
  lineHeight: 1,
};

const flag: React.CSSProperties = {
  width: 22,
  height: 14,
  flexShrink: 0,
  border: '1px solid rgba(0,0,0,.15)',
  display: 'block',
};

const SHOP = {
  wrap: { position: 'relative', maxWidth: 360 } as React.CSSProperties,
  row: { display: 'flex', border: '2px solid var(--shop-fg)' } as React.CSSProperties,
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '0 12px',
    background: 'transparent',
    border: 0,
    borderRight: '2px solid var(--shop-fg)',
    color: 'var(--shop-fg)',
    cursor: 'pointer',
    fontFamily: 'var(--mono)',
    fontSize: 14,
  } as React.CSSProperties,
  flag,
  dial: { fontFamily: 'var(--mono)', fontSize: 14 } as React.CSSProperties,
  caret: { fontSize: 10, opacity: 0.6 } as React.CSSProperties,
  input: { border: 0, flex: 1, minWidth: 0 } as React.CSSProperties,
  list: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    listStyle: 'none',
    margin: 0,
    padding: 0,
    background: 'var(--shop-bg)',
    border: '2px solid var(--shop-fg)',
    zIndex: 50,
    maxHeight: 320,
    overflowY: 'auto',
  } as React.CSSProperties,
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '10px 12px',
    background: 'transparent',
    border: 0,
    borderTop: '1px solid rgba(var(--shop-muted-rgb),.15)',
    color: 'var(--shop-fg)',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'var(--body)',
    fontSize: 14,
  } as React.CSSProperties,
  optionActive: { background: 'rgba(255,107,26,.12)' } as React.CSSProperties,
  optionSelected: { color: 'var(--orange)' } as React.CSSProperties,
  optionName: { flex: 1 } as React.CSSProperties,
  optionDial: {
    fontFamily: 'var(--mono)',
    fontSize: 13,
    color: 'rgba(var(--shop-muted-rgb),.7)',
  } as React.CSSProperties,
};

const ADMIN = {
  ...SHOP,
  wrap: { position: 'relative' } as React.CSSProperties,
  row: { display: 'flex', gap: 6 } as React.CSSProperties,
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '0 10px',
    background: 'var(--admin-card)',
    border: '1px solid var(--admin-line-strong)',
    borderRadius: 6,
    color: 'var(--admin-ink)',
    cursor: 'pointer',
    fontFamily: 'var(--mono)',
    fontSize: 13,
    minHeight: 34,
  } as React.CSSProperties,
  input: { flex: 1, minWidth: 0 } as React.CSSProperties,
  list: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    listStyle: 'none',
    margin: 0,
    padding: 0,
    background: 'var(--admin-card)',
    border: '1px solid var(--admin-line-strong)',
    borderRadius: 6,
    zIndex: 50,
    maxHeight: 320,
    overflowY: 'auto',
    boxShadow: '0 8px 24px rgba(0,0,0,.18)',
  } as React.CSSProperties,
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '8px 12px',
    background: 'transparent',
    border: 0,
    color: 'var(--admin-ink)',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'var(--body)',
    fontSize: 13,
  } as React.CSSProperties,
  optionActive: { background: 'rgba(255,107,26,.10)' } as React.CSSProperties,
  optionSelected: { color: 'var(--orange-deep)' } as React.CSSProperties,
  optionDial: {
    fontFamily: 'var(--mono)',
    fontSize: 12,
    color: 'var(--admin-muted)',
  } as React.CSSProperties,
};
